import React, { useState } from 'react';
import axios from 'axios';
import { ArrowRight, Mail, MapPin, CheckCircle2, AlertCircle, User2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LangContext';
import Reveal from './Reveal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INQUIRY_KEYS = [
  'construction_project',
  'electrical_engineering',
  'industrial_maintenance',
  'workforce_partnership',
  'agriculture_workforce',
  'construction_workforce',
  'general_business_inquiry',
];

const INITIAL = {
  full_name: '',
  company: '',
  email: '',
  phone: '',
  country: '',
  inquiry_type: 'general_business_inquiry',
  industry: '',
  message: '',
  website: '', // honeypot
};

export default function Contact() {
  const { t, lang } = useLang();
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm(INITIAL);
    } catch (err) {
      const msg = err?.response?.data?.detail || t('contact.error');
      setErrorMsg(Array.isArray(msg) ? msg.map((m) => m.msg).join(', ') : String(msg));
      setStatus('error');
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-24 md:py-40 border-t border-[#27272A]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left info */}
          <div className="lg:col-span-5">
            <Reveal className="section-index">{t('contact.index')}</Reveal>
            <Reveal as="h2" className="h-section text-[#F5F5F2] mt-4 reveal-delay-1">
              {t('contact.title')}
            </Reveal>
            <Reveal className="mt-6 text-[#A1A1AA] text-base md:text-lg leading-relaxed reveal-delay-2">
              {t('contact.sub')}
            </Reveal>

            <Reveal className="mt-12 border border-[#27272A] reveal-delay-3">
              <div className="p-6 border-b border-[#27272A] flex items-start gap-4">
                <User2 size={18} className="text-[#C8102E] mt-1" />
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-[#A1A1AA] uppercase">{t('contact.director_label')}</div>
                  <div className="mt-1 font-display text-xl text-[#F5F5F2]">{t('contact.director_name')}</div>
                </div>
              </div>
              <a
                href={`mailto:${t('contact.director_email')}`}
                data-testid="director-email-link"
                className="p-6 border-b border-[#27272A] flex items-start gap-4 hover:bg-[#101013] transition-colors"
              >
                <Mail size={18} className="text-[#C8102E] mt-1" />
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-[#A1A1AA] uppercase">Email</div>
                  <div className="mt-1 text-[#F5F5F2]">{t('contact.director_email')}</div>
                </div>
              </a>
              <div className="p-6 flex items-start gap-4">
                <MapPin size={18} className="text-[#C8102E] mt-1" />
                <div>
                  <div className="font-mono text-[10px] tracking-[0.3em] text-[#A1A1AA] uppercase">{t('contact.office_label')}</div>
                  <div className="mt-1 text-[#F5F5F2]">{t('contact.office_value')}</div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Right form */}
          <div className="lg:col-span-7">
            <Reveal className="reveal-delay-2">
              {status === 'success' ? (
                <div data-testid="contact-success" className="border border-[#C8102E]/50 bg-[#101013] p-10">
                  <CheckCircle2 className="text-[#C8102E]" size={32} />
                  <h3 className="mt-6 font-display text-3xl text-[#F5F5F2]">{t('contact.success_title')}</h3>
                  <p className="mt-3 text-[#A1A1AA] max-w-md">{t('contact.success_body')}</p>
                  <button
                    onClick={() => setStatus('idle')}
                    data-testid="contact-reset-button"
                    className="mt-8 btn-secondary"
                  >
                    {lang === 'es' ? 'Enviar otra solicitud' : 'Submit another inquiry'}
                    <ArrowRight size={14} className="arrow" />
                  </button>
                </div>
              ) : (
                <form onSubmit={onSubmit} data-testid="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="website"
                    value={form.website}
                    onChange={onChange}
                    autoComplete="off"
                    tabIndex={-1}
                    aria-hidden="true"
                    className="hidden"
                  />

                  <div>
                    <label className="field-label" htmlFor="full_name">{t('contact.fields.full_name')}</label>
                    <input data-testid="field-full-name" id="full_name" required minLength={2} className="field-input" name="full_name" value={form.full_name} onChange={onChange} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="company">{t('contact.fields.company')}</label>
                    <input data-testid="field-company" id="company" required className="field-input" name="company" value={form.company} onChange={onChange} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="email">{t('contact.fields.email')}</label>
                    <input data-testid="field-email" id="email" required type="email" className="field-input" name="email" value={form.email} onChange={onChange} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="phone">{t('contact.fields.phone')}</label>
                    <input data-testid="field-phone" id="phone" required className="field-input" name="phone" value={form.phone} onChange={onChange} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="country">{t('contact.fields.country')}</label>
                    <input data-testid="field-country" id="country" required className="field-input" name="country" value={form.country} onChange={onChange} />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="industry">{t('contact.fields.industry')}</label>
                    <input data-testid="field-industry" id="industry" required className="field-input" name="industry" value={form.industry} onChange={onChange} />
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label" htmlFor="inquiry_type">{t('contact.fields.inquiry_type')}</label>
                    <select data-testid="field-inquiry-type" id="inquiry_type" className="field-select" name="inquiry_type" value={form.inquiry_type} onChange={onChange}>
                      {INQUIRY_KEYS.map((k) => (
                        <option key={k} value={k}>{t(`contact.inquiry_options.${k}`)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="field-label" htmlFor="message">{t('contact.fields.message')}</label>
                    <textarea data-testid="field-message" id="message" required minLength={10} className="field-textarea" name="message" value={form.message} onChange={onChange} />
                  </div>

                  {status === 'error' && (
                    <div data-testid="contact-error" className="md:col-span-2 flex items-start gap-3 text-sm text-[#C8102E] border border-[#C8102E]/40 bg-[#1A0A0E] p-4">
                      <AlertCircle size={16} className="mt-0.5 shrink-0" />
                      <span>{errorMsg || t('contact.error')}</span>
                    </div>
                  )}

                  <div className="md:col-span-2 flex flex-col md:flex-row md:items-center justify-between gap-4 mt-2">
                    <div className="text-xs text-[#A1A1AA]">
                      {t('contact.privacy_notice')}{' '}
                      <Link to="/privacy" className="text-[#F5F5F2] hover:text-[#C8102E] underline underline-offset-4 decoration-[#27272A]" data-testid="privacy-inline-link">
                        {t('contact.privacy_link')}
                      </Link>
                      .
                    </div>
                    <button
                      data-testid="contact-submit-button"
                      type="submit"
                      disabled={status === 'sending'}
                      className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {status === 'sending' ? t('contact.sending') : t('contact.submit')}
                      <ArrowRight size={16} className="arrow" />
                    </button>
                  </div>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
